import {Anchor, Button, Group, Text, TextInput, Tooltip, createStyles} from '@mantine/core';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface NewPredictionTopBarProps {
  autoName: string;
  disabledReason: string | null;
  isSubmitting: boolean;
  batchName: string;
  onBack: () => void;
  onBatchNameChange: (name: string) => void;
  onSubmit: () => void;
  predictionCount: number;
}

export default function NewPredictionTopBar({
  autoName,
  disabledReason,
  isSubmitting,
  batchName,
  onBack,
  onBatchNameChange,
  onSubmit,
  predictionCount,
}: NewPredictionTopBarProps): JSX.Element {
  const {classes} = useStyles();

  const handleBatchNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onBatchNameChange(event.currentTarget.value);
  };

  return (
    <div className={classes.bar}>
      <div className={classes.left}>
        <Anchor className={classes.backLink} component="button" onClick={onBack} size="md" type="button">
          <Group noWrap={true} spacing={6}>
            <BenchlingIcon className={classes.backIcon} icon="arrow-left" />
            Back
          </Group>
        </Anchor>
      </div>
      <TextInput
        aria-label="Batch name"
        className={classes.batchNameInput}
        classNames={{input: classes.batchNameField}}
        maxLength={255}
        onChange={handleBatchNameChange}
        placeholder={autoName}
        size="lg"
        value={batchName}
      />
      <Group className={classes.right} noWrap={true} spacing="md">
        {predictionCount > 0 && (
          <Text color="dimmed" size="sm">
            {predictionCount} {pluralize(predictionCount, 'prediction')} expected
          </Text>
        )}
        <Tooltip disabled={disabledReason == null} label={disabledReason ?? undefined}>
          <span>
            <Button
              disabled={disabledReason != null}
              leftIcon={<BenchlingIcon icon="paper-plane" />}
              loading={isSubmitting}
              onClick={onSubmit}
              size="md"
            >
              Submit
            </Button>
          </span>
        </Tooltip>
      </Group>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  bar: {
    alignItems: 'center',
    display: 'grid',
    gap: theme.spacing.md,
    // Equal-width side columns keep the center column visually centered regardless of how
    // much content sits on either side. minmax(0, 1fr) lets the sides shrink without overflow.
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
  },
  left: {
    justifySelf: 'start',
    minWidth: 0,
  },
  right: {
    justifySelf: 'end',
    minWidth: 0,
  },
  backLink: {
    whiteSpace: 'nowrap',
  },
  backIcon: {
    fontSize: theme.fontSizes.md,
  },
  batchNameInput: {
    maxWidth: 560,
    width: '50vw',
  },
  batchNameField: {
    fontSize: theme.fontSizes.xl,
    fontWeight: 500,
  },
}));
